import { memo, useEffect, useMemo, useState } from 'react';
// Components
import { ArrowHeadDown, IconButton } from '@effizency/efz-design-system';
// MUI
import { Accordion, AccordionSummary } from '@mui/material';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';

import { StyledSidebarSection } from 'components/@efz/Sidebar/StylesSidebar';
import Tooltip from 'components/@efz/Tooltip';
import IntlMessages from 'components/util/IntlMessages';

// Stores
import { useSidebarStore } from 'stores/sidebar';

// Types/Interfaces
import { TSidebarEntry } from 'types/sidebar';

const SidebarSection = ({ label, icon, route, expandable, menu, dataTestId }: TSidebarEntry) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [isExpanded, setIsExpanded] = useState(false);

    const {
        sidebarOpen,
        actions: { setSidebarOpen },
    } = useSidebarStore();

    const expandHandler = async () => {
        if (!expandable) await navigate(route);
        else {
            !sidebarOpen && setSidebarOpen(true);
            setIsExpanded(!isExpanded);
        }
    };

    const isActiveSection = useMemo(() => {
        if (!expandable || !menu || !menu.length) return route === pathname;
        return (
            menu.some((entry) => entry.route === pathname) ||
            menu
                .flatMap((s) => s.menu)
                ?.flatMap((s) => s?.entries)
                .some((entry) => entry?.route === pathname)
        );
    }, [expandable, menu, pathname, route]);

    useEffect(() => {
        !sidebarOpen && setIsExpanded(false);
    }, [sidebarOpen]);

    return (
        <StyledSidebarSection $open={sidebarOpen} $expanded={isExpanded} data-test-id="sidebar-section-hey">
            {sidebarOpen && (
                <Accordion expanded={isExpanded} className="section-accordion">
                    <AccordionSummary
                        className={classNames('section-accordion-summary', {
                            active: isActiveSection,
                        })}
                        expandIcon={
                            expandable && (
                                <IconButton variant="secondary" size="xs" dataTestId="expand-section-btn" onClick={expandHandler}>
                                    <ArrowHeadDown size="xs" variant="secondary" iconBtn />
                                </IconButton>
                            )
                        }
                        onClick={expandHandler}
                        data-testid={dataTestId}
                    >
                        <div>
                            {icon}
                            <span>
                                <IntlMessages id={label} />
                            </span>
                        </div>
                    </AccordionSummary>
                </Accordion>
            )}
            {!sidebarOpen && (
                <button
                    className={classNames('section-collapsed clickable', {
                        active: isActiveSection,
                    })}
                    onClick={expandHandler}
                    data-testid={dataTestId}
                >
                    <Tooltip placement="left" type="default" title={<IntlMessages id={label} />} arrowPlacement="none">
                        {icon as React.ReactElement}
                    </Tooltip>
                </button>
            )}
        </StyledSidebarSection>
    );
};

export default memo(SidebarSection);
